<script setup lang="ts">
import type { User as AuthUser } from '@/auth/AuthProvider';
import { onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { routes } from '@/router/routes';
import { useUserStore } from '@/store/user';
import { State } from '@/utils';
import useSwitchOffline from 'ui/composables/useSwitchOffline';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import useResetQueryCache from '@/hooks/useResetQueryCache';
import { useOrganizationStore } from '@/store/organization';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCurrentUserQuery } from '@/services/useCurrentUser';
import useImpersonation from './hooks/useImpersonation';
import { useProgrammatic } from '@oruga-ui/oruga-next';
import * as Sentry from '@sentry/vue';
import { getAuthProvider } from './auth/AuthFactory';

const router = useRouter();
const route = useRoute();
const { oruga } = useProgrammatic();
const userStore = useUserStore();
const organizationStore = useOrganizationStore();
const { setPageTitle } = usePageTitle();
const useLocalTokenAuth = userStore.useLocalTokenAuth;
useSwitchOffline();

const currentUserQuery = useCurrentUserQuery({ enabled: false });
const organization = useImpersonation();

async function handleAuthStateChange(user: AuthUser | null) {
  Sentry.setUser(user?.email ? { email: user.email } : null);

  if (!user) {
    userStore.$patch({ currentUser: null, isAuthenticated: false });

    if (!useLocalTokenAuth) {
      organizationStore.$patch({ organizationImpersonation: '', organization: null });
    }

    const { requiresAuth } = router.currentRoute.value.meta;
    const isVerification = router.currentRoute.value.path === routes.emailVerification.fullPath;

    if (requiresAuth && !isVerification) {
      await router.replace({ path: routes.login.path });
    }
    return;
  }

  const { requiresAuth } = router.currentRoute.value.meta;
  if (!requiresAuth && router.currentRoute.value.fullPath !== routes.home.path) {
    try {
      userStore.$patch({ state: State.LOADING, isAuthenticated: true });
      const response = await currentUserQuery.refetch();
      userStore.setCurrentUser(response.data);
      await router.push({ path: routes.home.path });
    } catch (error) {
      userStore.$patch({ state: State.READY });
    } finally {
      userStore.$patch({ state: State.READY });
    }
  }
}

let unsubscribeAuth: (() => void) | undefined;
let disposed = false;

if (useLocalTokenAuth) {
  void handleAuthStateChange(null);
} else {
  void getAuthProvider()
    .init((user) => {
      if (!disposed) {
        handleAuthStateChange(user).catch((error) => Sentry.captureException(error));
      }
    })
    .then((unsubscribe) => {
      if (disposed) unsubscribe();
      else unsubscribeAuth = unsubscribe;
    })
    .catch((error) => Sentry.captureException(error));
}

onBeforeUnmount(() => {
  disposed = true;
  unsubscribeAuth?.();
});

useResetQueryCache();

watch(
  () => organizationStore.currentOrganizationName,
  (newOrgName) => {
    setPageTitle(newOrgName);
  },
  { immediate: true },
);
watch(
  () => route.path,
  () => {
    if (oruga && oruga.modal) {
      oruga.modal.closeAll();
    }
  },
);
</script>
<template>
  <router-view :key="route.meta.requiresAuth ? organization : 'auth'"></router-view>
  <VueQueryDevtools />
</template>
